import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import AdminLayout from "../components/AdminLayout";
import Pagination from "../components/Pagination";
import { 
  ClipboardList, Search, Download, CheckCircle, XCircle,
  Filter, MoreHorizontal, RefreshCw, Mail, Trash2, DollarSign,
  User, Phone, Heart, FileText, Camera, Calendar, MapPin, Eye,
  ExternalLink, Image, UserCircle, Loader2, CreditCard
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import axios from "axios";
import { toast } from "sonner";
import RefundDialog from "../components/RefundDialog";

const EmbeddedLayout = ({ children }) => <>{children}</>;

const AdminRegistrations = ({ embedded = false }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailRegistration, setDetailRegistration] = useState(null);
  const [paymentSchedule, setPaymentSchedule] = useState(null);
  const [loadingPaymentSchedule, setLoadingPaymentSchedule] = useState(false);
  const [chargingPayment, setChargingPayment] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    program: "all",
    status: "all",
    payment: "all"
  });
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchData();
  }, [token, page, pageSize, filters.program, filters.status, filters.payment]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build query params for server-side filtering
      const params = { page, page_size: pageSize };
      if (filters.program !== 'all') params.program_id = filters.program;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.payment !== 'all') params.payment_status = filters.payment;
      
      const [regsRes, progsRes] = await Promise.all([
        axios.get(`${API}/registrations`, { headers, params }),
        axios.get(`${API}/programs`, { headers, params: { page: 1, page_size: 100 } })
      ]);
      
      // Handle paginated response
      if (regsRes.data.items) {
        setRegistrations(regsRes.data.items);
        setTotal(regsRes.data.total);
        setTotalPages(regsRes.data.total_pages);
      } else {
        setRegistrations(Array.isArray(regsRes.data) ? regsRes.data : []);
        setTotal(regsRes.data.length || 0);
        setTotalPages(1);
      }
      
      // Handle programs response
      if (progsRes.data.items) {
        setPrograms(progsRes.data.items);
      } else {
        setPrograms(Array.isArray(progsRes.data) ? progsRes.data : []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSelectedIds([]);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
    setSelectedIds([]);
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPage(1); // Reset to first page when filters change
    setSelectedIds([]);
  };

  // Client-side search filtering (for instant search feel)
  const filteredRegistrations = useMemo(() => {
    if (!filters.search) return registrations;
    return registrations.filter(reg => {
      return `${reg.player?.first_name} ${reg.player?.last_name}`.toLowerCase().includes(filters.search.toLowerCase()) ||
        reg.program?.name?.toLowerCase().includes(filters.search.toLowerCase());
    });
  }, [registrations, filters.search]);

  const stats = useMemo(() => ({
    total: total,
    confirmed: filteredRegistrations.filter(r => r.status === 'confirmed').length,
    pending: filteredRegistrations.filter(r => r.status === 'pending').length,
    waitlisted: filteredRegistrations.filter(r => r.status === 'waitlisted').length,
    revenue: filteredRegistrations.filter(r => r.payment_status === 'paid').reduce((sum, r) => sum + (r.amount || 0), 0)
  }), [filteredRegistrations, total]);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredRegistrations.map(r => r.registration_id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      toast.error("Please select registrations first");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (action === 'confirm') {
        await Promise.all(selectedIds.map(id => 
          axios.put(`${API}/registrations/${id}`, { status: 'confirmed' }, { headers })
        ));
        toast.success(`${selectedIds.length} registration(s) confirmed`);
      } else if (action === 'cancel') {
        await Promise.all(selectedIds.map(id => 
          axios.put(`${API}/registrations/${id}`, { status: 'cancelled' }, { headers })
        ));
        toast.success(`${selectedIds.length} registration(s) cancelled`);
      } else if (action === 'email') {
        toast.info("Email feature coming soon");
        return;
      }
      
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to update registrations");
    }
  };

  const handleExportCSV = () => {
    const headers = ['Player', 'Email', 'Program', 'Division', 'Status', 'Payment', 'Amount', 'Date'];
    const rows = filteredRegistrations.map(reg => [
      `${reg.player?.first_name || ''} ${reg.player?.last_name || ''}`,
      reg.household_email || '',
      reg.program?.name || '',
      reg.division?.name || '',
      reg.status,
      reg.payment_status,
      reg.amount,
      new Date(reg.created_at).toLocaleDateString()
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `registrations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success("Export downloaded");
  };

  const handleOpenRefund = (registration) => {
    setSelectedRegistration(registration);
    setRefundDialogOpen(true);
  };

  const handleOpenDetail = async (registration) => {
    setDetailRegistration(registration);
    setDetailDialogOpen(true);
    setPaymentSchedule(null);
    
    // Fetch payment schedule if this registration has a payment plan
    if (registration.payment_plan && registration.payment_plan !== "Full Payment") {
      setLoadingPaymentSchedule(true);
      try {
        const response = await axios.get(
          `${API}/subscriptions/payment-plan/${registration.registration_id}/schedule`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPaymentSchedule(response.data);
      } catch (error) {
        console.error("Error fetching payment schedule:", error);
      } finally {
        setLoadingPaymentSchedule(false);
      }
    }
  };

  const handleChargePayment = async (paymentNumber) => {
    if (!detailRegistration) return;
    
    if (!confirm(`Are you sure you want to charge payment ${paymentNumber} now?`)) return;
    
    setChargingPayment(paymentNumber);
    try {
      await axios.post(
        `${API}/subscriptions/payment-plan/${detailRegistration.registration_id}/charge-now`,
        { payment_number: paymentNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Payment ${paymentNumber} charged successfully`);
      
      // Refresh payment schedule
      const response = await axios.get(
        `${API}/subscriptions/payment-plan/${detailRegistration.registration_id}/schedule`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPaymentSchedule(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to charge payment");
    } finally {
      setChargingPayment(null);
    }
  };

  const handleRetryFailedPayments = async () => {
    if (!detailRegistration) return;
    
    if (!confirm("Retry all failed payments for this registration?")) return;
    
    try {
      const response = await axios.post(
        `${API}/subscriptions/payment-plan/${detailRegistration.registration_id}/retry-failed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      
      // Refresh payment schedule
      const scheduleResponse = await axios.get(
        `${API}/subscriptions/payment-plan/${detailRegistration.registration_id}/schedule`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPaymentSchedule(scheduleResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to retry payments");
    }
  };

  const handleDownloadFile = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Downloading ${filename || 'file'}...`);
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  const handleRefundComplete = () => {
    fetchData(); // Refresh the data after refund
  };

  const handleDeleteRegistration = async (registration) => {
    if (!confirm(`Are you sure you want to permanently delete the registration for ${registration.player?.first_name} ${registration.player?.last_name}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/registrations/${registration.registration_id}`, { headers });
      toast.success("Registration deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete registration");
    }
  };

  const handleCancelRegistration = async (registration) => {
    if (!confirm(`Are you sure you want to cancel the registration for ${registration.player?.first_name} ${registration.player?.last_name}?`)) {
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/registrations/${registration.registration_id}/cancel`, {}, { headers });
      toast.success("Registration cancelled successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel registration");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-50 text-emerald-700';
      case 'pending': return 'bg-blue-50 text-blue-700';
      case 'waitlisted': return 'bg-amber-50 text-amber-700';
      case 'cancelled': return 'bg-rose-50 text-rose-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  if (loading) {
    return (
      <PageLayout title="Registrations">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-900"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="space-y-6 operations-page" data-testid="admin-registrations-page">
        {!embedded && <section className="operations-hero" aria-labelledby="registrations-heading">
          <div>
            <p className="platform-eyebrow">Registration operations / 02</p>
            <h1 id="registrations-heading">Know every player.</h1>
            <p>See who is confirmed, what needs attention, and where revenue stands.</p>
          </div>
          <div className="operations-signal operations-signal-lime" aria-label={`${stats.pending + stats.waitlisted} registrations need attention`}>
            <strong>{stats.pending + stats.waitlisted}</strong>
            <span>Need attention</span>
          </div>
        </section>}
        {/* Header with Bulk Actions */}
        <div className="operations-actions flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm text-slate-500">
              {selectedIds.length > 0 
                ? `${selectedIds.length} selected` 
                : `${filteredRegistrations.length} total registrations`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {selectedIds.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-2xl border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => handleBulkAction('confirm')}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Confirm
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-2xl border-rose-200 text-rose-700 hover:bg-rose-50"
                  onClick={() => handleBulkAction('cancel')}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-2xl border-slate-200"
                  onClick={() => handleBulkAction('email')}
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Email
                </Button>
                <div className="w-px h-6 bg-slate-200" />
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              className="rounded-2xl border-slate-200"
              onClick={handleExportCSV}
            >
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-2xl"
              onClick={fetchData}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="operations-metrics grid grid-cols-2 md:grid-cols-5">
          <div>
            <p className="text-2xl font-semibold text-slate-900">{stats.total}</p>
            <p className="text-sm text-slate-500">Total</p>
          </div>
          <div>
            <p className="text-2xl font-semibold text-emerald-600">{stats.confirmed}</p>
            <p className="text-sm text-slate-500">Confirmed</p>
          </div>
          <div>
            <p className="text-2xl font-semibold text-blue-600">{stats.pending}</p>
            <p className="text-sm text-slate-500">Pending</p>
          </div>
          <div>
            <p className="text-2xl font-semibold text-amber-600">{stats.waitlisted}</p>
            <p className="text-sm text-slate-500">Waitlisted</p>
          </div>
          <div>
            <p className="text-2xl font-semibold text-slate-900">${stats.revenue.toLocaleString()}</p>
            <p className="text-sm text-slate-500">Revenue</p>
          </div>
        </div>

        {/* Filters */}
        <div className="platform-card operations-toolbar p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Filters</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search players..."
                className="pl-9 h-10 rounded-xl border-slate-200"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <Select 
              value={filters.program} 
              onValueChange={(value) => handleFilterChange('program', value)}
            >
              <SelectTrigger className="h-10 rounded-xl border-slate-200">
                <SelectValue placeholder="All Programs" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">All Programs</SelectItem>
                {programs.map(prog => (
                  <SelectItem key={prog.program_id} value={prog.program_id}>{prog.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select 
              value={filters.status} 
              onValueChange={(value) => handleFilterChange('status', value)}
            >
              <SelectTrigger className="h-10 rounded-xl border-slate-200">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="waitlisted">Waitlisted</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select 
              value={filters.payment} 
              onValueChange={(value) => handleFilterChange('payment', value)}
            >
              <SelectTrigger className="h-10 rounded-xl border-slate-200">
                <SelectValue placeholder="All Payments" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Registrations Table */}
        <div className="platform-card operations-ledger overflow-hidden" data-testid="registrations-table">
          {filteredRegistrations.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-slate-500 border-b border-slate-100 bg-slate-50/50">
                      <th className="p-4 font-medium">
                        <Checkbox 
                          checked={selectedIds.length === filteredRegistrations.length && filteredRegistrations.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      </th>
                      <th className="p-4 font-medium">Player</th>
                      <th className="p-4 font-medium">Program</th>
                      <th className="p-4 font-medium">Division</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium">Payment</th>
                      <th className="p-4 font-medium">Amount</th>
                      <th className="p-4 font-medium">Date</th>
                      <th className="p-4 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRegistrations.map((reg) => (
                      <tr 
                        key={reg.registration_id} 
                        className={`operations-row border-b border-slate-50 last:border-0 transition-colors cursor-pointer ${
                          selectedIds.includes(reg.registration_id) ? 'bg-indigo-50/30' : ''
                        }`}
                        onClick={() => handleOpenDetail(reg)}
                        data-testid={`registration-row-${reg.registration_id}`}
                      >
                        <td className="p-4" onClick={(e) => e.stopPropagation()}>
                          <Checkbox 
                            checked={selectedIds.includes(reg.registration_id)}
                            onCheckedChange={(checked) => handleSelectOne(reg.registration_id, checked)}
                          />
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-slate-900">
                              {reg.player?.first_name} {reg.player?.last_name}
                            </p>
                            <p className="text-xs text-slate-500">{reg.household_email}</p>
                          </div>
                        </td>
                        <td className="p-4 text-slate-600">{reg.program?.name}</td>
                        <td className="p-4 text-slate-600">{reg.division?.name || '-'}</td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${getStatusBadge(reg.status)}`}>
                            {reg.status}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                            reg.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 
                            reg.payment_status === 'refunded' ? 'bg-amber-50 text-amber-700' :
                            reg.payment_status === 'partially_refunded' ? 'bg-orange-50 text-orange-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {reg.payment_status === 'partially_refunded' ? 'Partial Refund' : reg.payment_status}
                          </span>
                        </td>
                        <td className="p-4 font-medium text-slate-900">${reg.amount || 0}</td>
                        <td className="p-4 text-slate-500 text-sm">
                          {new Date(reg.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`actions-btn-${reg.registration_id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-xl">
                              <DropdownMenuItem onClick={() => handleOpenDetail(reg)}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/admin/households/${reg.household_id}`)}>
                                View Household
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/admin/programs/${reg.program_id}`)}>
                                View Program
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-emerald-600"
                                onClick={() => {
                                  setSelectedIds([reg.registration_id]);
                                  handleBulkAction('confirm');
                                }}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Confirm
                              </DropdownMenuItem>
                              {reg.status !== 'cancelled' && (
                                <DropdownMenuItem 
                                  className="text-amber-600"
                                  onClick={() => handleCancelRegistration(reg)}
                                  data-testid={`cancel-btn-${reg.registration_id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancel Registration
                                </DropdownMenuItem>
                              )}
                              {reg.payment_status === 'paid' && (
                                <DropdownMenuItem 
                                  className="text-blue-600"
                                  onClick={() => handleOpenRefund(reg)}
                                  data-testid={`refund-btn-${reg.registration_id}`}
                                >
                                  <DollarSign className="w-4 h-4 mr-2" />
                                  Issue Refund
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-rose-600"
                                onClick={() => handleDeleteRegistration(reg)}
                                data-testid={`delete-btn-${reg.registration_id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Registration
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <ClipboardList className="w-12 h-12 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-500 mb-2">No registrations found</p>
              <p className="text-sm text-slate-400">Try adjusting your filters</p>
            </div>
          )}
        </div>
      </div>

      {/* Refund Dialog */}
      <RefundDialog
        open={refundDialogOpen}
        onOpenChange={setRefundDialogOpen}
        registration={selectedRegistration}
        onRefundComplete={handleRefundComplete}
      />

      {/* Registration Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Registration Details
            </DialogTitle>
          </DialogHeader>
          
          {detailRegistration && (
            <div className="space-y-6" data-testid="registration-detail-dialog">
              {/* Player Info Section */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-start gap-4">
                  {/* Player Photo */}
                  <div className="flex-shrink-0">
                    {detailRegistration.player?.photo_url ? (
                      <div className="relative group">
                        <img
                          src={detailRegistration.player.photo_url}
                          alt={`${detailRegistration.player.first_name} ${detailRegistration.player.last_name}`}
                          className="w-24 h-24 rounded-xl object-cover border-2 border-white shadow-md"
                          data-testid="player-photo"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute inset-0 w-full h-full rounded-xl bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          onClick={() => handleDownloadFile(detailRegistration.player.photo_url, `${detailRegistration.player.first_name}_${detailRegistration.player.last_name}_photo.jpg`)}
                        >
                          <Download className="w-5 h-5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-slate-200 flex items-center justify-center">
                        <User className="w-10 h-10 text-slate-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Player Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-slate-900">
                      {detailRegistration.player?.first_name} {detailRegistration.player?.last_name}
                    </h3>
                    
                    <div className="mt-2 space-y-1.5 text-sm">
                      {detailRegistration.player?.date_of_birth && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span>Age: {calculateAge(detailRegistration.player.date_of_birth)} years</span>
                          <span className="text-slate-400">•</span>
                          <span>DOB: {new Date(detailRegistration.player.date_of_birth).toLocaleDateString()}</span>
                        </div>
                      )}
                      {detailRegistration.player?.gender && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <UserCircle className="w-4 h-4 text-slate-400" />
                          <span className="capitalize">{detailRegistration.player.gender}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Download Photo Button (if photo exists) */}
                    {detailRegistration.player?.photo_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 rounded-lg"
                        onClick={() => handleDownloadFile(detailRegistration.player.photo_url, `${detailRegistration.player.first_name}_${detailRegistration.player.last_name}_photo.jpg`)}
                        data-testid="download-photo-btn"
                      >
                        <Camera className="w-4 h-4 mr-1.5" />
                        Download Photo
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Registration Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Program</p>
                  <p className="font-medium text-slate-900">{detailRegistration.program?.name}</p>
                  {detailRegistration.division?.name && (
                    <p className="text-sm text-slate-600 mt-1">{detailRegistration.division.name}</p>
                  )}
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Status</p>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${getStatusBadge(detailRegistration.status)}`}>
                      {detailRegistration.status}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                      detailRegistration.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {detailRegistration.payment_status}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Household Contact Info */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  Contact Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="font-medium text-slate-900">{detailRegistration.household_email || 'N/A'}</p>
                  </div>
                  {detailRegistration.household_phone && (
                    <div>
                      <p className="text-slate-500">Phone</p>
                      <p className="font-medium text-slate-900">{detailRegistration.household_phone}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Emergency Contact */}
              {(detailRegistration.player?.emergency_contact_name || detailRegistration.player?.emergency_contact_phone) && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-rose-900 mb-3 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-rose-500" />
                    Emergency Contact
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-rose-600">Name</p>
                      <p className="font-medium text-rose-900">{detailRegistration.player.emergency_contact_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-rose-600">Phone</p>
                      <p className="font-medium text-rose-900">{detailRegistration.player.emergency_contact_phone || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Medical Information */}
              {(detailRegistration.player?.medical_notes || detailRegistration.player?.allergies) && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-amber-500" />
                    Medical Information
                  </h4>
                  <div className="space-y-3 text-sm">
                    {detailRegistration.player.medical_notes && (
                      <div>
                        <p className="text-amber-600">Medical Notes</p>
                        <p className="text-amber-900">{detailRegistration.player.medical_notes}</p>
                      </div>
                    )}
                    {detailRegistration.player.allergies && (
                      <div>
                        <p className="text-amber-600">Allergies</p>
                        <p className="text-amber-900">{detailRegistration.player.allergies}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Documents Acknowledged */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  Documents & Waivers
                </h4>
                
                {detailRegistration.documents_accepted && detailRegistration.documents_accepted.length > 0 ? (
                  <div className="space-y-2">
                    {detailRegistration.documents_accepted.map((doc, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200"
                        data-testid={`acknowledged-doc-${idx}`}
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-medium text-emerald-900">{doc}</span>
                        </div>
                        <span className="text-xs text-emerald-600">Acknowledged</span>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Acknowledged on {new Date(detailRegistration.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">No documents required for this registration</p>
                )}
              </div>
              
              {/* Add-ons / Merchandise */}
              {detailRegistration.addons && detailRegistration.addons.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-400" />
                    Add-ons & Merchandise
                  </h4>
                  <div className="space-y-2">
                    {detailRegistration.addons.map((addon, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-900">{addon.name}</span>
                          {addon.size && <span className="text-xs text-slate-500">({addon.size})</span>}
                          {addon.quantity > 1 && <span className="text-xs text-slate-500">x{addon.quantity}</span>}
                        </div>
                        <span className="text-sm font-medium text-slate-900">${addon.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Payment Summary */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-600">Total Amount</p>
                    <p className="text-2xl font-bold text-indigo-900">${detailRegistration.amount || 0}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-indigo-600">Registered</p>
                    <p className="text-sm font-medium text-indigo-900">
                      {new Date(detailRegistration.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Payment Plan Section */}
              {detailRegistration.payment_plan && detailRegistration.payment_plan !== "Full Payment" && (
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      Payment Plan: {detailRegistration.payment_plan}
                    </h4>
                    {paymentSchedule?.stats?.failed_count > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRetryFailedPayments}
                        className="text-orange-600 border-orange-200 hover:bg-orange-50 rounded-lg"
                        data-testid="retry-failed-payments-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Retry Failed
                      </Button>
                    )}
                  </div>
                  
                  {loadingPaymentSchedule ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : paymentSchedule ? (
                    <div className="space-y-3">
                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">
                            {paymentSchedule.stats.paid_count} of {paymentSchedule.stats.total_payments} payments
                          </span>
                          <span className="font-medium text-slate-900">
                            ${paymentSchedule.stats.total_paid} paid
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-emerald-500 h-2 rounded-full transition-all"
                            style={{ width: `${(paymentSchedule.stats.paid_count / paymentSchedule.stats.total_payments) * 100}%` }}
                          />
                        </div>
                      </div>
                      
                      {/* Payment List */}
                      {paymentSchedule.payment_schedule.map((payment) => (
                        <div 
                          key={payment.payment_number}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            payment.status === 'paid' 
                              ? 'bg-emerald-50 border-emerald-200' 
                              : payment.status === 'failed' 
                                ? 'bg-red-50 border-red-200'
                                : payment.status === 'retry_pending'
                                  ? 'bg-orange-50 border-orange-200'
                                  : 'bg-slate-50 border-slate-200'
                          }`}
                          data-testid={`payment-${payment.payment_number}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                              payment.status === 'paid' 
                                ? 'bg-emerald-500 text-white' 
                                : payment.status === 'failed'
                                  ? 'bg-red-500 text-white'
                                  : payment.status === 'retry_pending'
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-slate-300 text-slate-600'
                            }`}>
                              {payment.status === 'paid' ? '✓' : payment.payment_number}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">${payment.amount.toFixed(2)}</p>
                              <p className="text-xs text-slate-500">
                                {payment.status === 'paid' 
                                  ? `Paid ${new Date(payment.paid_at).toLocaleDateString()}`
                                  : `Due ${new Date(payment.due_date).toLocaleDateString()}`
                                }
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {payment.status === 'paid' && (
                              <span className="text-xs font-medium text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                                Paid
                              </span>
                            )}
                            {payment.status === 'failed' && (
                              <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full">
                                Failed
                              </span>
                            )}
                            {payment.status === 'retry_pending' && (
                              <span className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                                Retry Pending
                              </span>
                            )}
                            {payment.status === 'pending' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleChargePayment(payment.payment_number)}
                                disabled={chargingPayment === payment.payment_number}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50 rounded-lg"
                                data-testid={`charge-payment-${payment.payment_number}`}
                              >
                                {chargingPayment === payment.payment_number ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <CreditCard className="w-4 h-4 mr-1" />
                                    Charge Now
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Remaining Amount */}
                      {paymentSchedule.stats.total_remaining > 0 && (
                        <div className="pt-2 border-t border-slate-200 flex justify-between text-sm">
                          <span className="text-slate-600">Remaining Balance</span>
                          <span className="font-semibold text-slate-900">${paymentSchedule.stats.total_remaining.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">Payment schedule not available</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminRegistrations;
